import { useState, useRef, useEffect } from 'react'
import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Pages } from '@constants/routes.constant'
import { useLanguage } from '@contexts/language.context'

const HelpWidget = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { _ } = useLingui()
  const { currentLanguage } = useLanguage()
  const widgetRef = useRef<HTMLDivElement>(null)

  // Helper function to add language prefix to URLs
  const getUrlWithLang = (url: string): string => 
    `/${currentLanguage}${url}`

  const helpItems = [
    {
      id: 'whats-new',
      icon: <span className="flex h-6 w-6 items-center justify-center">✨</span>,
      label: <Trans>What's new?</Trans>,
      onClick: () => window.open(getUrlWithLang(Pages.HELP_NEWS_ALL), '_blank'),
    },
    {
      id: 'help-center',
      icon: <span className="flex h-6 w-6 items-center justify-center">📚</span>,
      label: <Trans>Help center</Trans>,
      onClick: () => window.open(getUrlWithLang(Pages.HELP), '_blank'),
    },
    {
      id: 'support',
      icon: <span className="flex h-6 w-6 items-center justify-center">💬</span>,
      label: <Trans>Contact support</Trans>,
      onClick: () => window.open(getUrlWithLang(Pages.HELP), '_blank'),
    },
  ]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  return (
    <div className="fixed right-6 bottom-6 z-50" ref={widgetRef}>
      <div className="relative">
        {isOpen && (
          <div className="absolute right-0 bottom-12 mb-2 w-64 overflow-hidden rounded-lg bg-white shadow-lg">
            <div className="border-b p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">
                  <Trans>Help and resources</Trans>
                </h3>
                <button onClick={toggleMenu} className="text-gray-400 hover:text-gray-500" aria-label={_(msg`Close menu`)}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-2">
              {helpItems.map((item) => (
                <button
                  key={item.id}
                  className="flex w-full items-center rounded-md p-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={item.onClick}
                >
                  <div className="mr-3 text-gray-500">{item.icon}</div>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={toggleMenu}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-600 shadow-lg hover:text-blue-600 focus:outline-none"
          aria-label={_(msg`Help and resources`)}
        >
          <QuestionMarkCircleIcon className="h-8 w-8" />
        </button>
      </div>
    </div>
  )
}

export default HelpWidget
